import type { Job } from "../../../../../../models";
import type { FeatureCollection } from "geojson";

/**
 * Fields to compare for common values in bulk edit.
 * Excludes unique identifiers and file data.
 */
const BULK_EDITABLE_FIELDS: (keyof Job)[] = [
	// Basic info
	"importGrowerName",
	"importFarmName",
	"importFieldName",
	// Order info
	"isReadyToSample",
	"dateFieldReadyEstimated",
	"collectBoundary",
	"fieldPriority",
	"submissionNotes",
	// Deal info
	"importDensity",
	"importDepth",
	"externalFrequencyOfMicros",
	"importLabName",
	"externalTestPackageId",
	"dealIds",
	// Lab info
	"labInstructions",
	"microsList",
	// Crop info
	"lastCrop",
	"nextCrop",
	"yieldGoal",
	// Contacts
	"branchIds",
	"salespersonIds",
	"newSalesperson",
	"growerPhoneNumber",
	"submitterName",
	"submitterEmail",
	// Sync codes
	"eventId",
	"labSubmissionCode",
	"fieldId",
	"externalId",
	"externalFarmId",
	"externalLabId",
	// Misc
	"isTestJob",
];

/**
 * Deeply compare two values for equality.
 * Handles arrays, objects, and primitives.
 */
function deepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;
	if (a == null || b == null) return a === b;
	if (typeof a !== typeof b) return false;

	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		return a.every((item, index) => deepEqual(item, b[index]));
	}

	if (typeof a === "object" && typeof b === "object") {
		const keysA = Object.keys(a as object);
		const keysB = Object.keys(b as object);
		if (keysA.length !== keysB.length) return false;
		return keysA.every((key) => deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
	}

	return false;
}

/**
 * Computes values that are shared by ALL jobs.
 *
 * For each field:
 * - If all jobs have the same value, include it in the result
 * - If jobs have different values, the field is omitted (undefined)
 *
 * @param jobs - Array of jobs to find common values from
 * @returns Partial<Job> with only the fields that have identical values across all jobs
 */
export function computeCommonValues(jobs: Job[]): Partial<Job> {
	if (jobs.length === 0) return {};
	if (jobs.length === 1) {
		// Single job - return all editable fields
		const result: Partial<Job> = {};
		for (const field of BULK_EDITABLE_FIELDS) {
			const value = jobs[0][field];
			if (value !== undefined) {
				(result as Record<string, unknown>)[field] = value;
			}
		}
		return result;
	}

	const result: Partial<Job> = {};
	const firstJob = jobs[0];

	for (const field of BULK_EDITABLE_FIELDS) {
		const firstValue = firstJob[field];

		// Check if all jobs have the same value for this field
		const allSame = jobs.every((job) => deepEqual(job[field], firstValue));

		if (allSame && firstValue !== undefined) {
			(result as Record<string, unknown>)[field] = firstValue;
		}
	}

	return result;
}

/**
 * Merges edited values into multiple jobs.
 * Only updates fields that were explicitly edited (non-undefined in editedValues).
 *
 * @param jobs - Original jobs to update
 * @param editedValues - Values to apply to all jobs
 * @returns New array of jobs with merged values
 */
export function applyBulkEdits(jobs: Job[], editedValues: Partial<Job>): Job[] {
	return jobs.map((job) => {
		const updatedJob = { ...job };

		for (const [key, value] of Object.entries(editedValues)) {
			if (value !== undefined) {
				(updatedJob as Record<string, unknown>)[key] = value;
			}
		}

		return updatedJob;
	});
}

/**
 * Combines geo-data from multiple jobs into a single FeatureCollection for map display.
 * Each feature is tagged with the job ID for identification.
 *
 * @param jobs - Jobs to extract geo-data from
 * @param geoJSONKey - Which geo-data key to extract (boundaryGeoJSON, pointsGeoJSON, zonesGeoJSON)
 * @returns Combined FeatureCollection with all features from all jobs
 */
export function combineGeoData(
	jobs: Job[],
	geoJSONKey: "boundaryGeoJSON" | "pointsGeoJSON" | "zonesGeoJSON",
): FeatureCollection {
	const combinedFeatures: FeatureCollection = {
		type: "FeatureCollection",
		features: [],
	};

	for (const job of jobs) {
		const geoData = job.files?.[geoJSONKey];
		if (geoData?.features) {
			for (const feature of geoData.features) {
				combinedFeatures.features.push({
					...feature,
					properties: {
						...feature.properties,
						_jobId: job.id,
						_jobName: job.importFieldName || job.id,
					},
				});
			}
		}
	}

	return combinedFeatures;
}

/**
 * Checks if a field has mixed values across the selected jobs.
 *
 * @param jobs - Jobs to check
 * @param field - Field name to check
 * @returns true if jobs have different values for this field
 */
export function hasMixedValues(jobs: Job[], field: keyof Job): boolean {
	if (jobs.length <= 1) return false;

	const firstValue = jobs[0][field];
	return !jobs.every((job) => deepEqual(job[field], firstValue));
}
